import React from 'react';
import { Container, Grid, Typography, Box } from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import cleaningMachineImg from '../assets/images/header-backgrounds/background1.png';
import pricingCarpetImg from '../assets/images/home/pricing-carpet.png';
import pricingTileImg from '../assets/images/home/pricing-tile.png';
import pricingUpholsteryImg from '../assets/images/home/pricing-upholstery.png';
import familyPic from '../assets/images/familypic.png';
import B1 from '../assets/images/before-after/B1.jpg';
import A1 from '../assets/images/before-after/A1.jpg';
import B2 from '../assets/images/before-after/B2.jpg';
import A2 from '../assets/images/before-after/A2.jpg';
import B3 from '../assets/images/before-after/B3.jpg';
import A3 from '../assets/images/before-after/A3.jpg';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import {
  Section,
  SectionTitle,
  ProcessCard,
  ProcessStep,
  PricingCard,
  FAQAccordion,
  WelcomeSection,
  WelcomeContent,
  StyledAccordionSummary,
  StyledAccordionDetails
} from '../components/styled/CommonStyles';

function HomePage() {
  const { currentTheme } = useTheme();

  return (
    <>
      <WelcomeSection backgroundImage={cleaningMachineImg}>
        <WelcomeContent>
          <Typography variant="h1" component="h1" sx={{ color: 'white', mb: 2 }}>
            Hawkins Carpet Cleaning
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, color: 'white', textAlign: 'left' }}>
            <strong>Hawkins Carpet Cleaning is a professional carpet cleaning service based in Owensboro, Kentucky. </strong>
            With years of experience in the industry, we specialize in revitalizing carpets to enhance the cleanliness and aesthetics of your home or business. Our unique value proposition lies in our commitment to using eco-friendly cleaning solutions that are safe for both your family and the environment, ensuring a deep clean that leaves carpets looking and feeling fresh.
          </Typography>
        </WelcomeContent>
      </WelcomeSection>

      <Section theme={currentTheme} sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <SectionTitle theme={currentTheme}>About Us</SectionTitle>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={familyPic}
                  alt="Hawkins Family"
                  style={{ maxWidth: '100%', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ color: currentTheme.primary, fontWeight: 'bold', mb: 2 }}>
                Making Your House As Good As New
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.6, color: currentTheme.text }}>
                Welcome to Hawkins Carpet Cleaning! My name is Brandon Hawkins, and I am the proud owner of this 
                family-run business. My wife, Michelle, and I have been together since 2011 and tied the knot in 2016. 
                Our family has since grown with the birth of our two wonderful daughters, one in 2018 and the other in 2021. 
                Before starting Hawkins Carpet Cleaning in 2019, I spent 10 years working at Glenmore. The decision to start 
                our own business was a leap of faith, driven by a passion for providing top-quality carpet cleaning services 
                while being environmentally conscious.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section theme={currentTheme}>
        <Container>
          <SectionTitle theme={currentTheme}>Our Process</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3">Inspection</Typography>
                  <Typography>We thoroughly inspect your carpets to identify any special needs or concerns.</Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3">Cleaning</Typography>
                  <Typography>Using state-of-the-art equipment and eco-friendly solutions for optimal results.</Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProcessCard theme={currentTheme}>
                <ProcessStep theme={currentTheme}>
                  <Typography variant="h5" component="h3">Final Inspection</Typography>
                  <Typography>We ensure your complete satisfaction with our quality guarantee.</Typography>
                </ProcessStep>
              </ProcessCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section theme={currentTheme} sx={{ bgcolor: 'background.default' }}>
        <Container>
          <SectionTitle theme={currentTheme}>Our Services</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <PricingCard theme={currentTheme}>
                <img src={pricingCarpetImg} alt="Carpet Cleaning" className="pricing-image" />
                <div className="pricing-content">
                  <Typography variant="h5" component="h3">Carpet Cleaning</Typography>
                  <ul>
                    <li>Deep cleaning</li>
                    <li>Stain removal</li>
                    <li>Odor elimination</li>
                    <li>Pet-friendly solutions</li>
                  </ul>
                </div>
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard theme={currentTheme}>
                <img src={pricingTileImg} alt="Tile & Grout Cleaning" className="pricing-image" />
                <div className="pricing-content">
                  <Typography variant="h5" component="h3">Tile & Grout</Typography>
                  <ul>
                    <li>Grout restoration</li>
                    <li>Deep cleaning</li>
                    <li>Sealing options</li>
                    <li>Commercial & residential</li>
                  </ul>
                </div>
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard theme={currentTheme}>
                <img src={pricingUpholsteryImg} alt="Upholstery Cleaning" className="pricing-image" />
                <div className="pricing-content">
                  <Typography variant="h5" component="h3">Upholstery</Typography>
                  <ul>
                    <li>Furniture cleaning</li>
                    <li>Stain removal</li>
                    <li>Odor treatment</li>
                    <li>Fabric protection</li>
                  </ul>
                </div>
              </PricingCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section theme={currentTheme}>
        <Container>
          <SectionTitle theme={currentTheme}>Before & After</SectionTitle>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 3,
                  width: '100%',
                  height: 400,
                  position: 'relative',
                  '& > div': {
                    height: '100% !important',
                    width: '100% !important'
                  }
                }}
              >
                <BeforeAfterSlider beforeImage={B1} afterImage={A1} />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 3,
                  width: '100%',
                  height: 400,
                  position: 'relative',
                  '& > div': {
                    height: '100% !important',
                    width: '100% !important'
                  }
                }}
              >
                <BeforeAfterSlider beforeImage={B2} afterImage={A2} />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 3,
                  width: '100%',
                  height: 400,
                  position: 'relative',
                  '& > div': {
                    height: '100% !important',
                    width: '100% !important'
                  }
                }}
              >
                <BeforeAfterSlider beforeImage={B3} afterImage={A3} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <SectionTitle>Pricing</SectionTitle>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <PricingCard>
                <img src={pricingCarpetImg} alt="Carpet Cleaning" className="pricing-image" />
                <div className="pricing-content">
                  <h3>Carpet Cleaning</h3>
                  <ul>
                    <li>3 Area Special $190</li>
                    <li>3 Area & Hallway $210</li>
                    <li>Per Room $70</li>
                    <li>Per hallway $30</li>
                    <li>Stair case $70</li>
                    <li>Walk in closet $30</li>
                    <li>Commercial Carpet FREE QUOTE needed</li>
                  </ul>
                </div>
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard>
                <img src={pricingTileImg} alt="Tile & Grout" className="pricing-image" />
                <div className="pricing-content">
                  <h3>Tile & Grout</h3>
                  <ul>
                    <li>Priced per sqft .75-1.00</li>
                    <li>Free Quotes</li>
                  </ul>
                </div>
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard>
                <img src={pricingUpholsteryImg} alt="Upholstery" className="pricing-image" />
                <div className="pricing-content">
                  <h3>Upholstery</h3>
                  <ul>
                    <li>Oversized Sectional $220</li>
                    <li>Sectional $190</li>
                    <li>Sofa $160</li>
                    <li>Loveseat $135</li>
                    <li>Recliner $115</li>
                    <li>Auto Upholstery Free Quote</li>
                  </ul>
                </div>
              </PricingCard>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <Section theme={currentTheme} sx={{ bgcolor: 'background.default' }}>
        <Container>
          <SectionTitle theme={currentTheme}>Frequently Asked Questions</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">How often should I have my carpets cleaned?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    We recommend professional carpet cleaning every 6-12 months for most homes. High-traffic areas or homes with pets may need more frequent cleaning.
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">How long does it take for carpets to dry?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    Typically, carpets are dry within 4-6 hours after cleaning. We use powerful equipment to minimize drying time.
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">Do you offer any guarantees?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    Yes, we offer a 100% satisfaction guarantee on all our services. If you're not completely satisfied, we'll come back and make it right.
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">What areas do you serve?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    We proudly serve Daviess and the surrounding counties. Contact us to confirm if we cover your location—we'd love to help refresh your home!
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">Are your cleaning products safe for children and pets?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    Absolutely! We use non-toxic, eco-friendly cleaning solutions that are safe for children, pets, and allergy-sensitive individuals. Your family's health and safety are our top priorities.
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">Do you offer emergency or same-day cleaning services?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    Yes! We understand accidents happen, which is why we offer emergency carpet cleaning services when available. Call us as soon as possible, and we'll do our best to accommodate you.
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">Can you remove tough stains like wine, coffee, or pet accidents?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    Yes! Our advanced enzyme-based stain removal solutions effectively break down tough stains, including wine, coffee, pet accidents, and more. We'll restore your carpets to their best condition!
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
            <Grid item xs={12}>
              <FAQAccordion theme={currentTheme}>
                <StyledAccordionSummary theme={currentTheme}>
                  <Typography variant="h6">What payment methods do you accept?</Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography>
                    We accept cash, credit/debit cards, and mobile payment options for your convenience. Our goal is to make the process smooth and hassle-free.
                  </Typography>
                </StyledAccordionDetails>
              </FAQAccordion>
            </Grid>
          </Grid>
        </Container>
      </Section>
    </>
  );
}

export default HomePage;
   